import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import CaseText from "@/components/CaseText";
import { ReadAloud } from "@/components/ReadAloud";
import api from "@/lib/api";
import { Search } from "lucide-react";

export default function Dictionary() {
  const [searchParams] = useSearchParams();
  const [terms, setTerms] = useState([]);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [cat, setCat] = useState("All");
  const [activeLetter, setActiveLetter] = useState(null);

  useEffect(() => {
    api.get("/dictionary").then((r) => setTerms(r.data));
  }, []);

  useEffect(() => {
    const qq = searchParams.get("q");
    if (qq !== null) setQuery(qq);
  }, [searchParams]);

  const CATEGORIES = useMemo(() => ["All", ...Array.from(new Set(terms.map((t) => t.category))).sort()], [terms]);

  const filtered = useMemo(() => {
    return terms.filter((t) => {
      const matchQ = t.term.toLowerCase().includes(query.toLowerCase()) || t.definition.toLowerCase().includes(query.toLowerCase());
      const matchC = cat === "All" || t.category === cat;
      return matchQ && matchC;
    });
  }, [terms, query, cat]);

  const letters = useMemo(() => Array.from(new Set(filtered.map((t) => t.term[0].toUpperCase()))).sort(), [filtered]);

  const scrollTo = (letter) => {
    setActiveLetter(letter);
    document.getElementById(`letter-${letter}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((t) => {
      const l = t.term[0].toUpperCase();
      (g[l] = g[l] || []).push(t);
    });
    return g;
  }, [filtered]);

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-widest font-mono text-primary">Reference</span>
        <h1 className="font-serif text-5xl mt-2 mb-3">Legal & Banking Dictionary</h1>
        <p className="text-muted-foreground max-w-2xl mb-10">
          Every key term across the tracks — now expanded with definitions from <span className="text-foreground">Black's Law Dictionary (Rev. 4th ed., 1968)</span>.
          Any case citation is clickable and pulls the opinion in the Case Law search.
        </p>

        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="dictionary-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search terms & definitions…"
              className="w-full bg-muted border border-input pl-10 pr-3 py-3 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`dict-cat-${c.toLowerCase()}`}
                className={`px-4 py-3 text-xs uppercase tracking-widest font-mono border transition-colors ${cat === c ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-[auto_1fr] gap-8">
          <div className="hidden lg:flex flex-col gap-1 sticky top-24 self-start">
            {letters.map((l) => (
              <button
                key={l}
                onClick={() => scrollTo(l)}
                data-testid={`letter-btn-${l}`}
                className={`w-8 h-8 text-xs font-mono border transition-colors ${activeLetter === l ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary hover:text-primary"}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="space-y-10">
            {Object.keys(grouped).sort().map((letter) => (
              <div key={letter} id={`letter-${letter}`}>
                <h2 className="font-serif text-3xl text-primary mb-4 border-b border-border pb-2">{letter}</h2>
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                  {grouped[letter].map((t) => (
                    <div key={t.term} className="bg-card border border-border p-6" data-testid={`term-${t.term.replace(/\s/g,"-").toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-2 gap-3">
                        <h3 className="font-mono text-lg text-foreground">{t.term}</h3>
                        <div className="flex items-center gap-2 shrink-0">
                          <ReadAloud label="" getText={() => `${t.term}. ${t.definition}`} />
                          <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground border border-border px-2 py-0.5">{t.category}</span>
                        </div>
                      </div>
                      <p className="text-sm text-foreground/85 leading-relaxed"><CaseText text={t.definition} /></p>
                      {t.manual_note && (
                        <p className="text-xs text-muted-foreground mt-3 border-l-2 border-primary pl-3 italic">
                          In these theories: <CaseText text={t.manual_note} />
                        </p>
                      )}
                      {t.source && (
                        <p className="text-[10px] font-mono text-muted-foreground/70 mt-3">— {t.source}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-muted-foreground">No terms match your search.</p>}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
